/* ============================================================
   mazo-offer-session.js — keeps the offer code on "Buy it now" checkouts.

   Owner, 2026-09-28: "put the code on earlier, as soon as an eligible signed-in shopper lands on the site, so it's
   there before they tap it ... Buy it now should be there".

   ⚠️ WHY THIS FILE EXISTS: A CODE ON THE CART DOES NOT REACH A "BUY IT NOW" CHECKOUT. Measured on the live store,
   no order placed:
     code on the cart via /cart/update.js (what the auto-apply does)  -> Buy it now checkout ₹1,650, NO code
     Shopify's shareable link /discount/10MAZO                        -> Buy it now checkout ₹1,485, 10MAZO −₹165
   Buy it now builds its own checkout from the one product and ignores the cart, but it DOES honour Shopify's
   session discount, which `/discount/CODE` stores as a `discount_code` cookie. So for a shopper who can use the
   offer, this file sets that session, with a background fetch and no navigation. The normal cart path is
   unchanged; the session also puts the code on the cart on its own (measured), which agrees with auto-apply.

   WHO GETS IT (the eligibility decision is made elsewhere, this file only acts on it):
     signed in + eligible  -> `<meta name="mazo-offer-session">`, rendered by layout/theme.liquid only when
                              auto-apply is on and the shopper passes the Theme settings > Offers audience rule
                              (first-order only -> zero orders). Same rule as mazo-cart-ship.liquid `eligible`.
     guest                 -> only once they have given their details in the offer popup (localStorage
                              `mazo_lead`, the same flag mazo-drawer.js offerReturn() uses).
   Shopify still checks the code's own rules at checkout (10MAZO: segment "Customers who haven't purchased",
   once per customer), so a session set for the wrong person is simply refused there.

   IT NEVER OVERRIDES THE SHOPPER:
     - a different code already on the cart   -> leave it alone (they chose it);
     - they removed ours with the X            -> the X sets `mazo_auto:'1'` on the cart (mazo-drawer.js) and this
                                                  file deletes the cookie; a non-empty cart with that flag and no
                                                  code means "removed", so it is not set again, in this session
                                                  or a later one;
     - they apply a different code by hand     -> the cookie is deleted, so Buy it now cannot bring ours back.
   ⚠️ Deleting from JS is enough: the cookie is not httpOnly (host-only on www.shopmazo.in), and after deleting it
   a Buy it now checkout came back at full price (measured).
   ⚠️ Limit: a shopper who lands and taps Buy it now inside the first fraction of a second, before the fetch
   returns, gets the old behaviour. The fetch runs at script parse, not on load, to keep that window small.
   ============================================================ */
(function () {
  'use strict';
  if (window.__mazoOfferSession) return;
  window.__mazoOfferSession = true;

  var SESSION_KEY = 'mazo_offer_session';   // sessionStorage: the code already set this session
  var OFF_KEY = 'mazo_offer_off';           // sessionStorage: shopper removed/replaced it this session
  var COOKIE = 'discount_code';

  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function root() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function same(a, b) { return String(a || '').toUpperCase() === String(b || '').toUpperCase(); }

  function candidate() {
    var meta = document.querySelector('meta[name="mazo-offer-session"]');
    if (meta && meta.content) return meta.content;                       // signed in + eligible
    var dlg = document.getElementById('MazoOfferDialog');                // only rendered for guests
    if (dlg && lsGet('mazo_lead') === '1') return dlg.getAttribute('data-mazo-offer-code') || '';
    return '';
  }

  function cookieValue() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  /* host-only is what Shopify sets here; the domain forms are belt and braces for a future domain change */
  function clearCookie() {
    var h = location.hostname, parts = h.split('.'), doms = ['', h, '.' + h];
    if (parts.length > 2) { var apex = parts.slice(-2).join('.'); doms.push(apex, '.' + apex); }
    doms.forEach(function (d) {
      document.cookie = COOKIE + '=; Max-Age=0; path=/' + (d ? '; domain=' + d : '');
    });
  }

  function run() {
    var code = candidate();
    if (!code || ssGet(OFF_KEY) === '1') return;
    if (ssGet(SESSION_KEY) === code && same(cookieValue(), code)) return;   // already set this session
    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = (cart && cart.discount_codes) || [];
        var live = codes.filter(function (c) { return c.applicable; });
        if (live.some(function (c) { return !same(c.code, code); })) return;           // their own code wins
        var removed = cart && cart.item_count > 0 && !live.length
          && cart.attributes && cart.attributes.mazo_auto === '1';
        if (removed) return;                                                           // they took it off
        return fetch(root() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root() + 'cart.js'),
          { credentials: 'same-origin' })
          .then(function () { ssSet(SESSION_KEY, code); });
      })
      .catch(function () {});   // a courtesy: failing silently leaves the old behaviour, never an error
  }

  /* The shopper's own discount actions. Bound in the CAPTURE phase so this runs whatever mazo-drawer.js does
     with the same click. */
  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    if (event.target.closest('[data-mazo-discount-clear]')) {
      clearCookie(); ssSet(OFF_KEY, '1'); ssSet(SESSION_KEY, '');
      return;
    }
    var apply = event.target.closest('[data-mazo-discount-apply]');
    if (apply) {
      var wrap = apply.closest('[data-mazo-discount]');
      var input = wrap && wrap.querySelector('[data-mazo-discount-input]');
      var typed = input ? input.value.trim() : '';
      if (typed && !same(typed, candidate())) { clearCookie(); ssSet(OFF_KEY, '1'); ssSet(SESSION_KEY, ''); }
    }
  }, true);
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' || !event.target.closest) return;
    var input = event.target.closest('[data-mazo-discount-input]');
    var typed = input ? input.value.trim() : '';
    if (typed && !same(typed, candidate())) { clearCookie(); ssSet(OFF_KEY, '1'); ssSet(SESSION_KEY, ''); }
  }, true);

  /* A guest who submits the popup becomes a candidate on the spot (mazo-drawer.js marks the lead in the same
     submit). Deferred a tick so a rejected (invalid) submit, which never marks the lead, is a no-op. */
  document.addEventListener('submit', function (event) {
    if (event.target.closest && event.target.closest('.mazo-offer-form')) setTimeout(run, 0);
  });

  run();
})();
